import { gql } from '@apollo/client';

import { BagStatus } from '../../bags/graphql/types/BagTagType';
import { SortingBatchBagType } from './sortingBatchDetail';

export enum SortingBatchStatusType {
    CREATED = 'CREATED',
    SORTING = 'SORTING',
    SORTING_ENDED = 'SORTING_ENDED',
    COMPLETED = 'COMPLETED',
    CANCELLED = 'CANCELLED',
}

export interface SortingBatchDestinationType {
    hubCode: string;
    hubId: string;
    isOwnHub: boolean;
    warehouseCode: string | null;
    warehouseId: string | null;
    warehouseName: string | null;
}

export const GET_SORTING_BATCH_DESTINATIONS = gql`
    query GetSortingBatchDestinations($warehouseId: Long!) {
        sortingBatchDestinations(warehouseId: $warehouseId) {
            hubCode
            hubId
            isOwnHub
            warehouseCode
            warehouseId
            warehouseName
        }
    }
`;

export interface GetSortingBatchDestinationsVariables {
    warehouseId: string;
}

export interface GetSortingBatchDestinationsResponse {
    sortingBatchDestinations: SortingBatchDestinationType[];
}

export interface SortingBatchServerNowType {
    serverNow: string;
    timezone: string | null;
}

export const GET_SORTING_BATCH_SERVER_NOW = gql`
    query GetSortingBatchServerNow($warehouseId: Long!) {
        sortingBatchServerNow(warehouseId: $warehouseId) {
            serverNow
            timezone
        }
    }
`;

export interface GetSortingBatchServerNowVariables {
    warehouseId: string;
}

export interface GetSortingBatchServerNowResponse {
    sortingBatchServerNow: SortingBatchServerNowType;
}

export interface SortingBatchType {
    id: string;
    batchName: string;
    warehouseId: string;
    fromSortingCode: string;
    destinationHubCode: string;
    destinationWhsCode: string | null;
    status: SortingBatchStatusType;
    origin: number;
    isVisible: boolean;
    /** epoch millis（Long 以字符串形式返回） */
    createdAt: string;
    creatorName: string | null;
    /** 创建批次的分拣机机器编号，人工创建时为空 */
    sorterCode: string | null;
    /** 批次当前库存包牌数（is_deleted=0 口径，已移出的不计入） */
    bagCount: number;
    /** 批次当前库存包裹数（is_deleted=0 口径，已移出的不计入） */
    parcelCount: number;
}

const SORTING_BATCH_FIELDS = gql`
    fragment SortingBatchFields on SortingBatchType {
        id
        batchName
        warehouseId
        fromSortingCode
        destinationHubCode
        destinationWhsCode
        status
        origin
        isVisible
        createdAt
        creatorName
        sorterCode
        bagCount
        parcelCount
    }
`;

export const GET_SORTING_BATCHES = gql`
    query GetSortingBatches($warehouseId: Long!, $status: SortingBatchStatusType) {
        sortingBatches(warehouseId: $warehouseId, status: $status) {
            ...SortingBatchFields
        }
    }
    ${SORTING_BATCH_FIELDS}
`;

export interface GetSortingBatchesVariables {
    warehouseId: string;
    status?: SortingBatchStatusType;
}

export interface GetSortingBatchesResponse {
    sortingBatches: SortingBatchType[];
}

export const GET_SORTING_BATCHES_BY_DESTINATION = gql`
    query GetSortingBatchesByDestination($warehouseId: Long!, $status: SortingBatchStatusType) {
        sortingBatchesByDestination(warehouseId: $warehouseId, status: $status) {
            ...SortingBatchFields
        }
    }
    ${SORTING_BATCH_FIELDS}
`;

export interface GetSortingBatchesByDestinationVariables {
    warehouseId: string;
    status?: SortingBatchStatusType;
}

export interface GetSortingBatchesByDestinationResponse {
    sortingBatchesByDestination: SortingBatchType[];
}

export const CREATE_SORTING_BATCHES = gql`
    mutation CreateSortingBatches($input: CreateSortingBatchesInput!) {
        createSortingBatches(input: $input) {
            batches {
                ...SortingBatchFields
            }
        }
    }
    ${SORTING_BATCH_FIELDS}
`;

export interface CreateSortingBatchesInput {
    warehouseId: string;
    destinationKeys: string[];
    /** epoch millis，以字符串形式传给后端 Long 入参 */
    batchTime: string;
}

export interface CreateSortingBatchesVariables {
    input: CreateSortingBatchesInput;
}

export interface CreateSortingBatchesResponse {
    createSortingBatches: {
        batches: SortingBatchType[];
    };
}

export const CANCEL_SORTING_BATCHES = gql`
    mutation CancelSortingBatches($input: CancelSortingBatchesInput!) {
        cancelSortingBatches(input: $input) {
            batches {
                ...SortingBatchFields
            }
        }
    }
    ${SORTING_BATCH_FIELDS}
`;

export interface CancelSortingBatchesInput {
    batchIds: string[];
}

export interface CancelSortingBatchesVariables {
    input: CancelSortingBatchesInput;
}

export interface CancelSortingBatchesResponse {
    cancelSortingBatches: {
        batches: SortingBatchType[];
    };
}

export const END_SORTING_BATCHES = gql`
    mutation EndSortingBatches($input: EndSortingBatchesInput!) {
        endSortingBatches(input: $input) {
            batches {
                ...SortingBatchFields
            }
            skippedBatchNames
        }
    }
    ${SORTING_BATCH_FIELDS}
`;

export interface EndSortingBatchesInput {
    batchIds: string[];
}

export interface EndSortingBatchesVariables {
    input: EndSortingBatchesInput;
}

export interface EndSortingBatchesResponse {
    endSortingBatches: {
        batches: SortingBatchType[];
        skippedBatchNames: string[];
    };
}

export const GET_SORTING_CODES_BY_DESTINATION = gql`
    query GetSortingCodesByDestination($batchIds: [Long!]!) {
        sortingCodesByDestination(batchIds: $batchIds) {
            batchId
            codes
        }
    }
`;

export interface GetSortingCodesByDestinationVariables {
    batchIds: string[];
}

export interface SortingBatchCodesType {
    batchId: string;
    codes: string[];
}

export interface GetSortingCodesByDestinationResponse {
    sortingCodesByDestination: SortingBatchCodesType[];
}

export const CREATE_SORTING_BATCH_BAGS = gql`
    mutation CreateSortingBatchBags($input: CreateSortingBatchBagsInput!) {
        createSortingBatchBags(input: $input) {
            bags {
                bagId
                bagNumber
                sortingCode
                status
                creationMethod
                parcelCount
            }
        }
    }
`;

export interface CreateSortingBatchBagsInput {
    batchId: string;
    sortingCodes: string[];
}

export interface CreateSortingBatchBagsVariables {
    input: CreateSortingBatchBagsInput;
}

export interface CreateSortingBatchBagsResponse {
    createSortingBatchBags: {
        bags: SortingBatchBagType[];
    };
}

export const GET_SORTING_BATCH_BAGS_FOR_PRINT = gql`
    query GetSortingBatchBagsForPrint($warehouseId: Long!, $batchIds: [Long!]!) {
        sortingBatchBagsForPrint(warehouseId: $warehouseId, batchIds: $batchIds) {
            batchId
            bags {
                bagId
                bagNumber
                sortingCode
                status
            }
        }
    }
`;

/** 列表页批量打印用：仅打印所需字段，不含详情页才需要的创建方式/包裹数汇总 */
export interface SortingBatchPrintableBagType {
    bagId: string;
    bagNumber: string;
    sortingCode: string | null;
    /** 包牌原有状态值 */
    status: BagStatus;
}

export interface SortingBatchBagsForPrintType {
    batchId: string;
    bags: SortingBatchPrintableBagType[];
}

export interface GetSortingBatchBagsForPrintVariables {
    warehouseId: string;
    batchIds: string[];
}

export interface GetSortingBatchBagsForPrintResponse {
    sortingBatchBagsForPrint: SortingBatchBagsForPrintType[];
}
